<template>
  <div class="historyrecord-container">
    <!-- 图表区域 -->
    <div class="chart-container">
      <div ref="chartRef" class="chart"></div>
      
      <!-- 轴范围控制工具栏 -->
      <div class="yaxis-toolbar">
        <el-dropdown @command="handleAxisCommand" trigger="click">
          <el-button size="small" type="primary" plain>
            {{ $t('message.historyRecord.yAxisControl') }} 
            <el-icon class="el-icon--right"><arrow-down /></el-icon>
          </el-button>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item command="showYAxisSettings">
                {{ $t('message.historyRecord.showAllYAxisSettings') }}
              </el-dropdown-item>
              <el-dropdown-item divided command="clearAllYAxis">
                {{ $t('message.historyRecord.clearAllYAxisRanges') }}
              </el-dropdown-item>
              <el-dropdown-item command="showXAxisControl" divided>
                {{ $t('message.historyRecord.showAllXAxisSettings') }}                
              </el-dropdown-item>
              <el-dropdown-item command="clearXAxisRange" divided>
                {{ $t('message.historyRecord.clearAllXAxisRanges') }}                
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </div>

    <!-- 筛选条件和数据表格区域 -->
    <div class="control-panel">
      <div class="filter-section">
        <!-- 时间选择区域（显示第一个时段） -->
        <div class="time-range-default">
          <div class="filter-row">
            <span class="section-index">{{ $t('message.historyRecord.section') }} {{ 1 }}</span>
            <el-select 
              v-model="displaySection.quickTime" 
              :placeholder="$t('message.historyRecord.selectTime')"
              @change="handleDisplayQuickTimeChange"
              size="default"
              style="width: 120px"
            >
              <el-option
                v-for="item in quickTimeOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
            
            <el-date-picker
              v-model="displaySection.timeRange"
              type="datetimerange"
              range-separator="-"
              :start-placeholder="$t('message.historyRecord.startTime')"
              :end-placeholder="$t('message.historyRecord.endTime')"
              value-format="YYYY-MM-DD HH:mm:ss"
              @change="handleDisplayTimeRangeChange"
              size="default"
              style="max-width: 400px; width: 100%;"
            />
            
            <!-- 多时段按钮 -->
            <el-button type="info" plain @click="openMultiTimeRangeDialog" size="default">
              <el-icon><Operation /></el-icon>
              {{ $t('message.historyRecord.multiTimeRange') }}
            </el-button>
            
            <!-- 查询和导出按钮 -->
            <el-button type="primary" @click="handleQuery" :loading="loading" size="default">
              {{ $t("message.historyRecord.query") }}
            </el-button>
            <el-button 
              type="primary" 
              :loading="exportLoading"
              @click="handleExport()"
              size="default"
            >
              {{ $t('message.historyRecord.exportVariables') }}
            </el-button>
            
            <!-- X轴变量选择 -->
            <el-select 
              v-model="xAxisVariable"
              :placeholder="$t('message.historyRecord.selectXAxisVariable')"
              clearable
              filterable
              @change="handleXAxisVariableChange"
              size="default"
              style="width: 180px"
            >
              <el-option
                v-for="variable in allVariables"
                :key="variable.name"
                :label="variable.display_name"
                :value="variable.name"
              />
            </el-select>
            
            <!-- X轴范围提示 -->
            <el-tag v-if="xAxisVariable && xAxisRangeInfo" type="info" size="default">            
               {{ $t('message.historyRecord.xAxisPointCount') }} {{ xAxisRangeInfo.min }} ~ {{ xAxisRangeInfo.max }} ({{ xAxisRangeInfo.count }}{{ $t('message.historyRecord.points') }})
            </el-tag>
          </div>
        </div>
      </div>
    </div>

    <!-- 多时段管理对话框 -->
    <el-dialog
      v-model="showMultiTimeRangeDialog"
      :title="$t('message.historyRecord.multiTimeRangeManagement')"
      width="700px"
      destroy-on-close
    >
      <div class="multi-time-range-container">
        <div class="dialog-header">
          <span class="dialog-title">{{ $t('message.historyRecord.timeRangeSections') }}</span>
          <el-button type="primary" size="small" @click="addTimeRangeSection">
            <el-icon><Plus /></el-icon>
            {{ $t('message.historyRecord.addTimeSection') }}
          </el-button>
        </div>
        
        <div class="time-ranges-list">
          <div 
            v-for="(section, index) in multiTimeRangeSections" 
            :key="section.id"
            class="time-range-section"
          >
            <div class="section-header">
              <span class="section-index">{{ $t('message.historyRecord.section') }} {{ index + 1 }}</span>
              <el-button 
                type="danger" 
                size="small" 
                link 
                @click="removeTimeRangeSection(index)"
                v-if="multiTimeRangeSections.length > 1"
              >
                <el-icon><Delete /></el-icon>
                {{ $t('message.historyRecord.delete') }}
              </el-button>
            </div>
            
            <div class="section-controls">
              <el-select 
                v-model="section.quickTime" 
                :placeholder="$t('message.historyRecord.selectTime')"
                @change="(val) => handleSectionQuickTimeChange(section, val, index)"
                size="default"
                style="width: 120px"
              >
                <el-option
                  v-for="item in quickTimeOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
              
              <el-date-picker
                v-model="section.timeRange"
                type="datetimerange"
                range-separator="-"
                :start-placeholder="$t('message.historyRecord.startTime')"
                :end-placeholder="$t('message.historyRecord.endTime')"
                value-format="YYYY-MM-DD HH:mm:ss"
                @change="() => handleSectionTimeRangeChange(section, index)"
                size="default"
                style="max-width: 400px; width: 100%;"
              />
            </div>
          </div>
        </div>
        
        <div class="dialog-footer-tip">
          <el-alert
            :title="$t('message.historyRecord.multiTimeRangeTip')"
            type="info"
            :closable="false"
            show-icon
          />
        </div>
      </div>
      
      <template #footer>
        <el-button @click="showMultiTimeRangeDialog = false">
          {{ $t('message.historyRecord.cancel') }}
        </el-button>
        <el-button type="primary" @click="confirmMultiTimeRanges">
          {{ $t('message.historyRecord.confirm') }}
        </el-button>
      </template>
    </el-dialog>

    <!-- Y轴范围设置对话框 -->
    <el-dialog
      v-model="showYAxisRangeDialog"
      :title="$t('message.historyRecord.setYAxisRange', { name: currentYAxisVariable?.display_name || '' })"
      width="400px"
    >
      <el-form :model="tempYAxisRange" label-width="80px">
        <el-form-item :label="$t('message.historyRecord.minValue')">
          <el-input-number
            v-model="tempYAxisRange.min"
            :precision="3"
            :step="0.1"
            :placeholder="$t('message.historyRecord.minValue')"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item :label="$t('message.historyRecord.maxValue')">
          <el-input-number
            v-model="tempYAxisRange.max"
            :precision="3"
            :step="0.1"
            :placeholder="$t('message.historyRecord.maxValue')"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item v-if="yAxisManualRange[currentYAxisVariable?.name]?.enabled">
          <el-button type="danger" size="small" @click="resetYAxisRange(currentYAxisVariable?.name)">
            {{ $t('message.historyRecord.reset') }}
          </el-button>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showYAxisRangeDialog = false">
          {{ $t('message.historyRecord.cancel') }}
        </el-button>
        <el-button type="primary" @click="saveYAxisRange">
          {{ $t('message.historyRecord.confirm') }}
        </el-button>
      </template>
    </el-dialog>

    <!-- X轴范围设置对话框（单时段） -->
    <el-dialog
      v-model="showXAxisRangeDialog"
      :title="$t('message.historyRecord.xAxisRangeManagement')"      
      width="500px"
    >
      <el-form label-width="90px">
        <el-form-item :label="$t('message.historyRecord.startTimer')" >
          <el-date-picker
            v-model="tempXAxisRange.min"
            type="datetime"
            :placeholder="$t('message.historyRecord.placeholderStartTimer')" 
            value-format="YYYY-MM-DD HH:mm:ss"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item :label="$t('message.historyRecord.endTimer')">
          <el-date-picker
            v-model="tempXAxisRange.max"
            type="datetime"
            :placeholder="$t('message.historyRecord.placeholderEndTimer')"  
            value-format="YYYY-MM-DD HH:mm:ss"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item v-if="xAxisManualRange.enabled">
          <el-button type="danger" size="small" @click="resetXAxisRange" plain>
            {{ $t('message.historyRecord.reset') }}
          </el-button>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showXAxisRangeDialog = false">
          {{ $t('message.historyRecord.cancel') }}
        </el-button>
        <el-button type="primary" @click="saveXAxisRange">
          {{ $t('message.historyRecord.confirm') }}
        </el-button>
      </template>
    </el-dialog>

    <!-- X轴留白设置对话框（多时段专用） -->
    <el-dialog
      v-model="showXAxisPaddingDialog"
      :title="$t('message.historyRecord.xAxisPaddingControl')"
      width="400px"
    >
      <el-form label-width="100px">
        <el-form-item :label="$t('message.historyRecord.leftPadding')">
          <el-input-number
            v-model="tempXAxisPadding.left"
            :min="0"
            :max="1000"
            :step="10"
            controls-position="right"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item :label="$t('message.historyRecord.rightPadding')">
          <el-input-number
            v-model="tempXAxisPadding.right"
            :min="0"
            :max="1000"
            :step="10"
            controls-position="right"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item :label="$t('message.historyRecord.sectionGap')">
          <el-input-number
            v-model="tempXAxisPadding.sectionGap"
            :min="0"
            :max="1000"
            :step="10"
            controls-position="right"
            style="width: 100%"
          />
        </el-form-item>

        <el-form-item>
          <el-button size="small" @click="resetXAxisPadding" plain>
            {{ $t('message.historyRecord.resetPadding') }}
          </el-button>
        </el-form-item>
        <el-alert
          :title="$t('message.historyRecord.paddingTip')"
          type="info"
          :closable="false"
          show-icon
        />
      </el-form>
      <template #footer>
        <el-button @click="showXAxisPaddingDialog = false">
          {{ $t('message.historyRecord.cancel') }}
        </el-button>
        <el-button type="primary" @click="saveXAxisPadding">
          {{ $t('message.historyRecord.confirm') }}
        </el-button>
      </template>
    </el-dialog>
    
    <!-- Y轴范围管理面板 -->
    <el-drawer
      v-model="showYAxisRangePanel"
      :title="$t('message.historyRecord.yAxisRangeManagement')"
      direction="rtl"
      size="400px"
    >
      <div class="yaxis-range-list">
        <div 
          v-for="variable in currentDisplayVariables" 
          :key="variable.name"
          class="yaxis-range-item"
        >
          <div class="range-item-header">
            <span class="variable-name">{{ variable.display_name }}</span>
            <el-tag 
              :type="yAxisManualRange[variable.name]?.enabled ? 'success' : 'info'"
              size="small"
            >
              {{ yAxisManualRange[variable.name]?.enabled ? $t('message.historyRecord.manual') : $t('message.historyRecord.auto') }}
            </el-tag>
          </div>
          <div class="range-item-values" v-if="yAxisManualRange[variable.name]?.enabled">
            <span>{{ $t('message.historyRecord.minValue') }}: {{ yAxisManualRange[variable.name].min.toFixed(4) }}</span>
            <span>{{ $t('message.historyRecord.maxValue') }}: {{ yAxisManualRange[variable.name].max.toFixed(4) }}</span>
          </div>
          <div class="range-item-actions">
            <el-button 
              size="small" 
              type="primary" 
              plain
              @click="openYAxisRangeSetting(variable)"
            >
              {{ $t('message.historyRecord.setYAxisRange', { name: '' }) }}
            </el-button>
            <el-button 
              v-if="yAxisManualRange[variable.name]?.enabled"
              size="small" 
              type="danger" 
              plain
              @click="resetYAxisRange(variable.name)"
            >
              {{ $t('message.historyRecord.reset') }}
            </el-button>
          </div>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script setup>
import {
  ref,
  reactive,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
  computed,
  defineProps
} from "vue";
import * as echarts from "echarts";
import { ElMessage } from "element-plus";
import { Plus, Delete, Operation } from '@element-plus/icons-vue';
import { getCompanyDeviceTree } from "@/api/CompanyManager";
import { useI18n } from "vue-i18n";
import { queryHistoryWhenAllData, exportHisVariablesByFile, formatValueRemove0, formatValueToDateTime, formatValueRemove2, queryServiceTime } from "@/api/DeviceManagement";
import pako from 'pako';

const { t } = useI18n();
const loading = ref(false);
const exportLoading = ref(false);
const deviceList = ref([]);
const deviceTreeOptions = ref([]);
const selectedVarNames = ref([]);
const currentDisplayVariables = ref([]);
const quickTimeOptions = ref([]);
const chartRef = ref(null);
const cachedHistoryData = ref(new Map());
const currentDeviceId = ref(null);
const isInitialLoad = ref(true);

// 多时段相关变量
const multiTimeRangeSections = ref([]);
let nextSectionId = 1;

// 当前显示的时段（时段1，用于界面显示）
const displaySection = ref({
  id: null,
  quickTime: '24Hour',
  timeRange: [],
  customDate: null
});

// 多时段管理对话框相关变量
const showMultiTimeRangeDialog = ref(false);

// X轴变量选择
const xAxisVariable = ref('');
const xAxisRangeInfo = ref(null);

// X轴范围控制相关变量
const xAxisManualRange = ref({ min: null, max: null, enabled: false });
const showXAxisRangeDialog = ref(false);
const tempXAxisRange = ref({ min: '', max: '' });

// ===== 新增：X轴留白控制相关变量（多时段专用） =====
const xAxisPadding = ref({ left: 0, right: 0,sectionGap: 300 });
const tempXAxisPadding = ref({ left: 0, right: 0 , sectionGap: 300});
const showXAxisPaddingDialog = ref(false);

// Y轴范围控制相关变量
const yAxisManualRange = ref({});
const showYAxisRangeDialog = ref(false);
const showYAxisRangePanel = ref(false);
const currentYAxisVariable = ref(null);
const tempYAxisRange = ref({ min: '', max: '' });

// 存储Y轴范围数据
const yAxisRanges = ref({});
// 存储图例选择状态
const legendSelectedState = ref({});

let chartInstance = null;

const pagination = reactive({
  currentPage: 1,
  pageSize: 100000,
  total: 0,
});

const props = defineProps({
  varDevices: {
    type: Object,
    required: true,
    default: () => ({})
  },
  projectName: {
    type: String,
    default: ''
  },
  deviceId: {
    type: [String, Number],
    default: null
  }
});

// 所有变量
const allVariables = computed(() => {
  const variables = [];
  for (const [deviceCode, varObjects] of Object.entries(props.varDevices)) {
    varObjects.forEach(varObj => {
      variables.push({
        name: varObj.name,
        mult: varObj.mult,
        maximum: varObj.maximum,
        minimum: varObj.minimum,
        var_decimal: varObj.var_decimal,
        display_name: varObj.title,
        deviceCode: deviceCode,
        companyId: varObj.companyId
      });
    });
  }
  return variables;
});

// 根据选中的变量重新构建 varDevices
const filteredVarDevices = computed(() => {
  const result = {};
  
  if (selectedVarNames.value.length === 0) {
    return props.varDevices;
  }
  
  for (const [deviceCode, varObjects] of Object.entries(props.varDevices)) {
    const filteredVars = varObjects.filter(varObj => 
      selectedVarNames.value.includes(varObj.name)
    );
    
    if (filteredVars.length > 0) {
      result[deviceCode] = filteredVars;
    }
  }
  
  return result;
});

// 获取设备变量名映射
const getDeviceVariableMap = () => {
  const deviceMap = new Map();
  for (const [deviceCode, varObjects] of Object.entries(filteredVarDevices.value)) {
    const companyId = varObjects.length > 0 ? varObjects[0].companyId : 0;
    
    const var_names = varObjects.map(varObj => ({
      var_name: varObj.name,
      mult: varObj.mult,
      var_decimal: varObj.var_decimal,
      minimum: varObj.minimum,
      maximum: varObj.maximum
    }));
    
    deviceMap.set(deviceCode, {
      var_names: var_names,
      companyId: companyId
    });
  }
  return deviceMap;
};

// 监听外部传入的变量设备数据变化
watch(
  () => [props.varDevices, props.deviceId],
  ([newVarDevices, newDeviceId]) => {
    if (newDeviceId) {
      currentDeviceId.value = newDeviceId;
    }
    
    if (Object.keys(newVarDevices).length > 0) {
      selectedVarNames.value = allVariables.value.map(v => v.name);
      
      if (isInitialLoad.value) {
        nextTick(() => {
          initializeTimeRanges();
          isInitialLoad.value = false;
        });
      }
    }
  },
  { immediate: true, deep: true }
);

// 加载选项
const loadOptions = () => { 
  quickTimeOptions.value = [
    { value: '24Hour', label: t('message.historyRecord.24hour') },
    { value: 'today', label: t('message.historyRecord.today') },
    { value: 'yesterday', label: t('message.historyRecord.yesterday') },
    { value: 'sevenDays', label: t('message.historyRecord.sevenDays') },
    { value: 'custom', label: t('message.historyRecord.timeRange') }
  ];
};

const getHourParm = () => {
  const deviceVariableMap = getDeviceVariableMap();
  let devices = [];
  let companyTp = 0;
  for (const [deviceCode, deviceData] of deviceVariableMap) {
    const { var_names, companyId } = deviceData;      
    if (!var_names || var_names.length === 0) continue;
    const varNameStrings = var_names.map(item => item.var_name);
    devices.push({
      device_codes: deviceCode,
      names: varNameStrings
    });
    companyTp = companyId;
  }

  const params = {
    lang: localStorage.getItem('lang') || 'zh-CN',                    
    deviceInfo: devices,
    companyId: companyTp,
    Type: 0,      
    pageNum: 1,
    pageSize: 300,
  };
  return params;
}

// 时间范围函数
const get24HourRange = async (baseDate = null) => {  
  const params = getHourParm();
  const res = await queryServiceTime(params);

  if (!(res.success && res.data)) {
    ElMessage.warning(t('message.historyRecord.timeTip'));
  }

  let end = (res.success && res.data) 
    ? new Date(res.data.serviceTime.replace(' ', 'T'))
    : new Date();
  
  if (baseDate) {
    end = new Date(baseDate);
  }
  
  const start = new Date(end.getTime() - 24 * 60 * 60 * 1000);
  return [formatLocalDateTime(start), formatLocalDateTime(end)];
};

const getTodayTimeRange = (baseDate = null) => {
  const now = baseDate ? new Date(baseDate) : new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
  return [formatLocalDateTime(start), formatLocalDateTime(end)];
};

const getYesterdayTimeRange = (baseDate = null) => {
  const now = baseDate ? new Date(baseDate) : new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const start = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 0, 0, 0);
  const end = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59);
  return [formatLocalDateTime(start), formatLocalDateTime(end)];
};

const getSevenDaysTimeRange = (baseDate = null) => {
  const now = baseDate ? new Date(baseDate) : new Date();
  const sevenDaysAgo = new Date(now);
  sevenDaysAgo.setDate(now.getDate() - 7);
  const start = new Date(sevenDaysAgo.getFullYear(), sevenDaysAgo.getMonth(), sevenDaysAgo.getDate(), 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), now.getMinutes(), now.getSeconds());
  return [formatLocalDateTime(start), formatLocalDateTime(end)];
};

// 同步显示时段到多时段第一个时段
const syncDisplayToFirstSection = () => {
  if (multiTimeRangeSections.value.length > 0 && displaySection.value.timeRange && displaySection.value.timeRange.length === 2) {
    const firstSection = multiTimeRangeSections.value[0];
    firstSection.timeRange = [...displaySection.value.timeRange];
    firstSection.quickTime = displaySection.value.quickTime;
  }
};

// 同步多时段第一个时段到显示时段 - 修复版本，确保完整同步
const syncFirstSectionToDisplay = () => {
  if (multiTimeRangeSections.value.length > 0) {
    const firstSection = multiTimeRangeSections.value[0];
    if (firstSection && firstSection.timeRange && firstSection.timeRange.length === 2) {
      displaySection.value.timeRange = [...firstSection.timeRange];
      displaySection.value.quickTime = firstSection.quickTime;
      displaySection.value.id = firstSection.id;
      displaySection.value.customDate = firstSection.customDate;
    }
  }
};

// 初始化时间范围
const initializeTimeRanges = async () => {
  const defaultRange = await get24HourRange();
  // 初始化多时段列表，默认只有一个时段
  multiTimeRangeSections.value = [
    {
      id: nextSectionId++,
      quickTime: '24Hour',
      timeRange: defaultRange,
      customDate: null
    }
  ];
  // 同步到显示时段
  syncFirstSectionToDisplay();
};

// 处理显示时段的快速时间选择
const handleDisplayQuickTimeChange = async (value) => {
  if (value === '24Hour') {
    displaySection.value.timeRange = await get24HourRange();
  } else if (value === 'today') {
    displaySection.value.timeRange = getTodayTimeRange();
  } else if (value === 'yesterday') {
    displaySection.value.timeRange = getYesterdayTimeRange();
  } else if (value === 'sevenDays') {
    displaySection.value.timeRange = getSevenDaysTimeRange();
  } else if (value === 'custom') {
    displaySection.value.timeRange = [];
  }
  displaySection.value.quickTime = value;
  
  // 同步到多时段第一个时段
  syncDisplayToFirstSection();
};

// 处理显示时段的时间范围变化
const handleDisplayTimeRangeChange = () => {
  displaySection.value.quickTime = 'custom';
  // 同步到多时段第一个时段
  syncDisplayToFirstSection();
};

// 打开多时段管理对话框
const openMultiTimeRangeDialog = () => {
  // 确保多时段列表与显示时段同步
  if (multiTimeRangeSections.value.length === 0) {
    multiTimeRangeSections.value = [
      {
        id: nextSectionId++,
        quickTime: displaySection.value.quickTime,
        timeRange: [...displaySection.value.timeRange],
        customDate: null
      }
    ];
  } else if (multiTimeRangeSections.value.length > 0) {
    // 同步显示时段到第一个时段
    const firstSection = multiTimeRangeSections.value[0];
    firstSection.timeRange = [...displaySection.value.timeRange];
    firstSection.quickTime = displaySection.value.quickTime;
  }
  showMultiTimeRangeDialog.value = true;
};

// 添加时段区间
const addTimeRangeSection = async () => {
  try {
    // 获取当前第一个时段的时间范围作为新时段的默认值
    let defaultRange;
    if (multiTimeRangeSections.value.length > 0 && 
        multiTimeRangeSections.value[0].timeRange && 
        multiTimeRangeSections.value[0].timeRange.length === 2) {
      defaultRange = [...multiTimeRangeSections.value[0].timeRange];
    } else {
      defaultRange = await get24HourRange();
    }
    
    multiTimeRangeSections.value.push({
      id: nextSectionId++,
      quickTime: '24Hour',
      timeRange: defaultRange,
      customDate: null
    });
    ElMessage.success(t('message.historyRecord.sectionAdded'));
  } catch (error) {
    console.error('添加时段失败:', error);
    ElMessage.error(t('message.historyRecord.addSectionFailed'));
  }
};

// 删除时段区间 - 修复版本，确保删除后正确更新显示
const removeTimeRangeSection = (index) => {
  if (multiTimeRangeSections.value.length <= 1) {
    ElMessage.warning(t('message.historyRecord.atLeastOneSection'));
    return;
  }
  
  const isDeletingFirst = (index === 0);
  //const deletedId = multiTimeRangeSections.value[index]?.id;
  multiTimeRangeSections.value.splice(index, 1);
  
  // 如果删除的是第一个时段，需要将新的第一个时段同步到显示时段
  if (isDeletingFirst && multiTimeRangeSections.value.length > 0) {
    syncFirstSectionToDisplay();
  }
  
  ElMessage.success(t('message.historyRecord.sectionDeleted'));
};

// 处理单个时段的快速时间选择
const handleSectionQuickTimeChange = async (section, value, index) => {
  try {
    if (value === '24Hour') {
      section.timeRange = await get24HourRange(section.customDate);
    } else if (value === 'today') {
      section.timeRange = getTodayTimeRange(section.customDate);
    } else if (value === 'yesterday') {
      section.timeRange = getYesterdayTimeRange(section.customDate);
    } else if (value === 'sevenDays') {
      section.timeRange = getSevenDaysTimeRange(section.customDate);
    } else if (value === 'custom') {
      section.timeRange = [];
    }
    section.quickTime = value;
    
    // 如果是第一个时段变化，同步到显示时段
    if (index === 0) {
      syncFirstSectionToDisplay();
    }
  } catch (error) {
    console.error('时段时间更新失败:', error);
    ElMessage.warning(t('message.historyRecord.timeUpdateFailed'));
  }
};

// 处理单个时段的时间范围变化
const handleSectionTimeRangeChange = (section, index) => {
  section.quickTime = 'custom';
  
  // 如果是第一个时段变化，同步到显示时段
  if (index === 0) {
    syncFirstSectionToDisplay();
  }
};

// 确认多时段设置 - 修复版本
const confirmMultiTimeRanges = () => {
  // 过滤出有效的时间范围
  const validRanges = multiTimeRangeSections.value.filter(
    section => section.timeRange && section.timeRange.length === 2
  );
  
  if (validRanges.length === 0) {
    ElMessage.warning(t('message.historyRecord.noValidTimeRange'));
    return;
  }
  
  // 确保显示时段与第一个时段同步
  syncFirstSectionToDisplay();
  
  showMultiTimeRangeDialog.value = false;
  
  ElMessage.success(t('message.historyRecord.multiTimeRangeSet', { count: validRanges.length }));
  
  // 清空缓存并重新查询
  cachedHistoryData.value.clear();
  pagination.currentPage = 1;
  handleQuery();
};

// 获取当前使用的时间范围（所有时段）- 添加验证和排序
const getAllTimeRangesForQuery = () => {
  const validSections = multiTimeRangeSections.value.filter(
    section => section.timeRange && section.timeRange.length === 2
  );
  
  if (validSections.length === 0) {
    return [];
  }
  
  // 按 id 排序，确保顺序一致
  return validSections
    .sort((a, b) => a.id - b.id)
    .map(section => ({
      start: section.timeRange[0],
      end: section.timeRange[1],
      id: section.id,
      index: multiTimeRangeSections.value.findIndex(s => s.id === section.id)
    }));
};

// 检查是否有有效的时间范围
const hasValidTimeRanges = () => {
  return multiTimeRangeSections.value.some(
    section => section.timeRange && section.timeRange.length === 2
  );
};

// 获取所有时段的总小时数
const getTotalHours = () => {
  const ranges = getAllTimeRangesForQuery();
  let totalHours = 0;
  for (const range of ranges) {
    const startTime = new Date(range.start);
    const endTime = new Date(range.end);
    const diffInMs = endTime - startTime;
    totalHours += diffInMs / (1000 * 60 * 60);
  }
  return totalHours;
};

// 清除图表
const clearChart = () => {
  if (chartInstance) {
    chartInstance.clear();
  }
};

// 图表初始化
const initChart = () => {
  if (!chartRef.value) return;
  
  try {
    chartInstance = echarts.init(chartRef.value);
    
    const option = {
      title: {
        text: t("message.reaTimePlot.deviceVariableMonitor"),
      },
      tooltip: {
        trigger: "axis",
      },
      legend: {
        data: [],
      },
      grid: {
        left: "3%",
        right: "40px",
        bottom: "15%",
        containLabel: true,
      },
      xAxis: {
        type: "time",
        boundaryGap: false,
        axisLabel: {
          rotate: 0,
          fontSize: 15,
          margin: 8,
        }
      },
      yAxis: {
        type: "value",
      },
      series: [],
      dataZoom: [
        { 
          type: "inside", 
          xAxisIndex: [0],
          start: 0, 
          end: 100 
        },
        { 
          type: "slider",
          xAxisIndex: [0],
          start: 0, 
          end: 100,
          bottom: 10,
          height: 20,
          show: true
        }
      ]
    };

    chartInstance.setOption(option);
  } catch (error) {
    console.error('Chart initialization failed:', error);
  }
};

// 根据索引获取颜色
const getColorByIndex = (index) => {
  const colors = [
    '#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de',
    '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc', '#ff9f7f',
    '#1e90ff', '#32cd32', '#ff4500', '#6a5acd', '#ff69b4',
    '#00ced1', '#ffa500', '#8a2be2', '#5f9ea0', '#d2691e',
    '#6495ed', '#dc143c', '#00ff7f', '#ff1493', '#00bfff',
    '#696969', '#ff8c00', '#7cfc00', '#da70d6', '#cd5c5c',
    '#4682b4', '#9acd32', '#ff6347', '#8470ff', '#ff6eb4',
    '#00eeee', '#ff7f50', '#9370db', '#66cdaa', '#f08080'
  ];
  return colors[index % colors.length];
};

// 十六进制颜色转RGB函数
const hexToRgb = (hex) => {
  hex = hex.replace(/^#/, '');
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `${r}, ${g}, ${b}`;
};

// 计算Y轴数据范围（考虑手动设置）
const calculateYAxisRange = (data, variableName) => {
  const manualRange = yAxisManualRange.value[variableName];
  if (manualRange && manualRange.enabled) {
    return {
      min: manualRange.min,
      max: manualRange.max
    };
  }
  
  const values = data
    .map(item => parseFloat(item[variableName]))
    .filter(value => !isNaN(value));
  
  if (values.length === 0) {
    return { min: 0, max: 1 };
  }
  
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;
  const padding = range * 0.1 || 0.1;
  return {
    min: min - padding,
    max: max + padding
  };
};

// 创建动态多Y轴配置
const createDynamicYAxisConfig = (variables, processedData) => {
  const yAxisConfig = [];
  
  for (let i = 0; i < variables.length; i++) {
    const variable = variables[i];
    
    const isLeft = i % 2 === 0;
    const axisIndex = Math.floor(i / 2);
    const offset = axisIndex * 60;
    
    const range = calculateYAxisRange(processedData, variable.name);
    yAxisRanges.value[variable.name] = range;
    
    const verticalName = variable.display_name.split('').join('\n');
    
    yAxisConfig.push({
      type: 'value',
      name: verticalName,
      position: isLeft ? 'left' : 'right',
      offset: offset,
      axisLine: {
        show: true,
        lineStyle: {
          color: getColorByIndex(i),
          width: 1.5
        },
        onZero: false
      },
      axisTick: {
        show: true,
        lineStyle: {
          color: getColorByIndex(i)
        }
      },
      axisLabel: {
        formatter: function(value) {
          if (Math.abs(value) < 0.001 && value !== 0) {
            return value.toExponential(3);
          } else if (Math.abs(value) < 1) {
            return value.toFixed(4);
          } else if (Math.abs(value) < 10) {
            return value.toFixed(3);
          } else if (Math.abs(value) < 100) {
            return value.toFixed(2);
          } else if (Math.abs(value) < 1000) {
            return value.toFixed(1);
          } else if (value >= 1000000) {
            return (value / 1000000).toFixed(1) + 'M';
          } else if (value >= 1000) {
            return (value / 1000).toFixed(1) + 'k';
          }
          return Number(value.toFixed(0)).toString();
        },
        color: getColorByIndex(i),
        fontSize: 11,
        show: true,
        margin: isLeft ? 15 : 8,
        padding: [0, isLeft ? -10 : 0, 0, isLeft ? 0 : 0],
        align: isLeft ? 'right' : 'left',
        verticalAlign: 'middle'
      },
      nameTextStyle: {
        color: getColorByIndex(i),
        fontSize: 10,
        fontWeight: 'bold',
        align: 'center',
        lineHeight: 16,
        verticalAlign: 'top',
        padding: isLeft ? [50, 0, 0, 15] : [0, 15, 0, 0]
      },
      nameLocation: 'end',
      nameGap: isLeft ? 35 : -30,
      splitLine: {
        show: false
      },
      min: range.min,
      max: range.max,
      scale: false,
      boundaryGap: ['10%', '10%'],
      z: 100 + i
    });
  }
  
  return yAxisConfig;
};
 

// 图表更新方法 - 同一变量在不同时段使用相同颜色【带聚合功能】
const updateChart = (data) => {
  if (!chartInstance || !currentDisplayVariables.value.length) return;

  try {
    if (currentDisplayVariables.value.length > 20) {
      console.warn('变量数量较多，可能会影响图表性能');
    }

    // 按时段分组数据
    const dataBySection = new Map();
    data.forEach(item => {
      const sectionId = item.sectionId;
      if (!dataBySection.has(sectionId)) {
        dataBySection.set(sectionId, {
          sectionIndex: item.sectionIndex,
          data: []
        });
      }
      dataBySection.get(sectionId).data.push(item);
    });

    // 转数组按时段序号升序
    const sectionArr = Array.from(dataBySection.values()).sort((a, b) => a.sectionIndex - b.sectionIndex);
    console.log('所有时段数组', sectionArr);

    // 线条颜色、形状池
    const symbolShapes = ['circle', 'diamond', 'triangle', 'rect', 'roundRect', 'pin', 'arrow'];
    const colorPalette = [
      '#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de',
      '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc', '#ff9f7f',
      '#1e90ff', '#32cd32', '#ff4500', '#6a5acd', '#ff69b4',
      '#00ced1', '#ffa500', '#8a2be2', '#5f9ea0', '#d2691e',
      '#6495ed', '#dc143c', '#00ff7f', '#ff1493', '#00bfff',
      '#696969', '#ff8c00', '#7cfc00', '#da70d6', '#cd5c5c',
      '#4682b4', '#9acd32', '#ff6347', '#8470ff', '#ff6eb4',
      '#00eeee', '#ff7f50', '#9370db', '#66cdaa', '#f08080'
    ];

    const allSeriesData = [];

    // -------------- 判断是否多时段压缩空白 --------------
    const validTimeRanges = getAllTimeRangesForQuery();
    const needCompactMultiSection = validTimeRanges.length >= 2;    
    const sectionGap = xAxisPadding.value.sectionGap !== undefined   ? xAxisPadding.value.sectionGap   : (tempXAxisPadding.value.sectionGap || 300);
    const sectionStartIndex = new Map();
    const sectionAggregatedData = new Map();
    let currentIndex = 0;

    // ===== 聚合函数 =====
    const aggregateData = (list, maxPoints = 2000) => {
      if (list.length <= maxPoints) {
        return list.slice();
      }
      
      const result = [];
      const totalPoints = list.length;
      
      result.push({ ...list[0] });
      
      const targetPoints = maxPoints - 2;
      const step = (totalPoints - 1) / (targetPoints + 1);
      
      for (let i = 1; i <= targetPoints; i++) {
        const idx = Math.round(i * step);
        if (idx > 0 && idx < totalPoints - 1) {
          result.push({ ...list[idx] });
        }
      }
      
      result.push({ ...list[totalPoints - 1] });
      
      result.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      
      console.log(`聚合: 原始${totalPoints}条 -> 采样${result.length}条`);
      return result;
    };

    // ===== 第一步：对每个时段的数据进行聚合，并计算起始位置 =====
    sectionArr.forEach((secInfo) => {
      const sortedData = [...secInfo.data].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      
      const maxPoints = 10000;
      const aggregatedData = aggregateData(sortedData, maxPoints);
      
      sectionAggregatedData.set(secInfo.sectionIndex, aggregatedData);
      
      sectionStartIndex.set(secInfo.sectionIndex, currentIndex);
      console.log(`时段${secInfo.sectionIndex + 1} 起始索引`, currentIndex, "原始点数", sortedData.length, "聚合点数", aggregatedData.length);
      
      currentIndex += aggregatedData.length + sectionGap;
    });

    // ===== 第二步：遍历变量、时段生成折线系列 =====
    currentDisplayVariables.value.forEach((variable, varIndex) => {
      const baseColor = colorPalette[varIndex % colorPalette.length];
      let sectionCounter = 0;

      const sortedSectionEntries = Array.from(dataBySection.entries())
        .sort((a, b) => a[1].sectionIndex - b[1].sectionIndex);

      sortedSectionEntries.forEach(([sectionId, sectionInfo]) => {
              console.warn('sectionId',sectionId);
        const sectionIndex = sectionInfo.sectionIndex;
        const shapeIndex = sectionCounter % symbolShapes.length;
        const symbolType = symbolShapes[shapeIndex];
        
        const processedData = sectionAggregatedData.get(sectionIndex) || [];
        const startIdx = sectionStartIndex.get(sectionIndex) || 0;
        const seriesDataPoints = [];

        if (needCompactMultiSection) {
          processedData.forEach((item, idx) => {
            const value = parseFloat(item[variable.name]);
            if (!isNaN(value)) {
              seriesDataPoints.push([startIdx + idx, value]);
            }
          });
        } else {
          processedData.forEach((item) => {
            const value = parseFloat(item[variable.name]);
            if (!isNaN(value)) {
              const ts = new Date(item.timestamp).getTime();
              seriesDataPoints.push([ts, value]);
            }
          });
        }

        if (seriesDataPoints.length === 0) {
          sectionCounter++;
          return;
        }

        const seriesName = `${variable.display_name} (${t('message.historyRecord.section')} ${sectionIndex + 1})`;
        const isSelected = legendSelectedState.value[seriesName] !== undefined ? legendSelectedState.value[seriesName] : true;

        allSeriesData.push({
          name: seriesName,
          type: "line",
          yAxisIndex: varIndex,
          symbol: isSelected ? symbolType : "none",
          symbolSize: isSelected ? 6 : 0,
          smooth: false,
          lineStyle: {
            width: 1.5,
            color: baseColor,
            opacity: isSelected ? 1 : 0.2
          },
          itemStyle: {
            color: baseColor,
            borderColor: baseColor,
            borderWidth: 0,
            opacity: isSelected ? 1 : 0.2
          },
          data: seriesDataPoints,
          connectNulls: false,
          emphasis: {
            disabled: false,
            lineStyle: { width: 2.5, opacity: 1 },
            itemStyle: { borderWidth: 2, borderColor: baseColor }
          }
        });
        sectionCounter++;
      });
    });

    if (allSeriesData.length === 0) {
      console.warn('无图表数据');
      return;
    }

    // 多Y轴配置
    const yAxisConfig = createDynamicYAxisConfig(currentDisplayVariables.value, data);
    const legendData = allSeriesData.map(s => s.name);
    const initialLegendSelected = {};
    legendData.forEach(name => {
      initialLegendSelected[name] = legendSelectedState.value[name] !== undefined ? legendSelectedState.value[name] : true;
    });

    // Y轴缩放滑块
    const yAxisDataZoom = [];
    const yAxisSliderDataZoom = [];
    const yAxisToSliderDistance = 20;
    const sliderSpacing = 14;
    currentDisplayVariables.value.forEach((_, index) => {
      yAxisDataZoom.push({ type: "inside", yAxisIndex: index, start: 0, end: 100, zoomLock: false });
      const sliderRight = yAxisToSliderDistance + index * sliderSpacing;
      const yAxisColor = getColorByIndex(index);
      yAxisSliderDataZoom.push({
        type: "slider",
        yAxisIndex: index,
        start: 0,
        end: 100,
        right: sliderRight,
        width: 10,
        show: true,
        zoomLock: false,
        brushSelect: false,
        fillerColor: `rgba(${hexToRgb(yAxisColor)}, 0.2)`,
        borderColor: yAxisColor,
        handleSize: 6,
        handleStyle: { color: yAxisColor },
        backgroundColor: 'rgba(240, 242, 245, 0.8)',
        textStyle: { color: '#606266', fontSize: 15 },
        dataBackground: { lineStyle: { color: yAxisColor, opacity: 0.3 }, areaStyle: { color: yAxisColor, opacity: 0.1 } }
      });
    });

    const totalSlidersWidth = currentDisplayVariables.value.length * 10 + (currentDisplayVariables.value.length - 1) * 6;
    const gridRight = yAxisToSliderDistance + totalSlidersWidth + 60;
    const leftYAxisCount = currentDisplayVariables.value.filter((_, i) => i % 2 === 0).length;
    const gridLeft = leftYAxisCount === 0 ? '3%' : `${Math.max(24, leftYAxisCount * 15)}px`;
    const legendTop = legendData.length > 5 ? '40px' : '0px';
    const gridTop = legendData.length > 5 ? '80px' : '50px';

    // ===================== X轴配置 =====================
    let xAxisConfig = {};
    if (needCompactMultiSection) {
      const labelTicks = [];
      sectionArr.forEach((secInfo) => {
        const idx = sectionStartIndex.get(secInfo.sectionIndex);
        labelTicks.push({ value: idx, label: `${t('message.historyRecord.section')}${secInfo.sectionIndex + 1}` });
      });
      console.log('X轴标签列表', labelTicks);
      
      const tickPositions = labelTicks.map(t => t.value);
      
      // ===== 使用较大的阈值确保匹配 =====
      const threshold = Math.max(300, sectionGap * 0.5);

      // 计算实际数据最大位置
      const lastSection = sectionArr[sectionArr.length - 1];
      const lastAggregatedData = sectionAggregatedData.get(lastSection.sectionIndex) || [];
      const lastStartIdx = sectionStartIndex.get(lastSection.sectionIndex) || 0;
      const actualMaxX = lastStartIdx + lastAggregatedData.length - 1;
      console.log('actualMaxX:', actualMaxX);

      // 修改后（保持不变，因为它们已经是从 xAxisPadding 读取的）：
      const leftPadding = xAxisPadding.value.left || 0;
      const rightPadding = xAxisPadding.value.right || 0;

      xAxisConfig = {
        type: "value",
        boundaryGap: false,
        min: 0 - leftPadding,
        max: actualMaxX + rightPadding,
        axisLabel: {
          fontSize: 11,
          rotate: 30,
          interval: function (value) {
            for (const pos of tickPositions) {
              if (Math.abs(value - pos) < threshold) {
                return 0;
              }
            }
            return 999999;
          },
          formatter: function (value) {
            for (const tick of labelTicks) {

              if (Math.abs(value - tick.value) < threshold) {
                return tick.label;
              }              
            }
            return '';
          },
          showMinLabel: true,
          showMaxLabel: true
        },
        axisLine: { lineStyle: { color: '#dcdfe6' } },
        axisTick: {
          show: true,
          interval: function (value) {
            for (const pos of tickPositions) {
              if (Math.abs(value - pos) < threshold) {
                return 0;
              }
            }
            return 999999;
          }
        },
        splitLine: { show: false }
      };
    } else {
      // 单时段时间轴
      let allTimestamps = [];
      data.forEach(item => {
        const ts = new Date(item.timestamp).getTime();
        if (!isNaN(ts)) allTimestamps.push(ts);
      });
      allTimestamps.sort((a, b) => a - b);

      xAxisConfig = {
        type: "time",
        boundaryGap: false,
        min: 'dataMin',
        max: 'dataMax',
        scale: true,
        axisLabel: {
          rotate: 0,
          formatter: (value) => {
            const date = new Date(value);
            if (isNaN(date.getTime())) return '';
            const h = String(date.getHours()).padStart(2, '0');
            const m = String(date.getMinutes()).padStart(2, '0');
            if (h === '00' && m === '00') return `${date.getMonth() + 1}/${date.getDate()}`;
            if (m === '00') return `${h}:00`;
            return '';
          },
          fontSize: 12,
          margin: 8,
          interval: 'auto'
        },
        axisLine: { lineStyle: { color: '#dcdfe6' } },
        axisTick: { lineStyle: { color: '#dcdfe6' } }
      };

      if (xAxisManualRange.value.enabled && xAxisManualRange.value.min && xAxisManualRange.value.max) {
        const minTs = new Date(xAxisManualRange.value.min).getTime();
        const maxTs = new Date(xAxisManualRange.value.max).getTime();
        xAxisConfig.min = minTs;
        xAxisConfig.max = maxTs;
      }
    }

    // dataZoom
    let dataZoomConfig = [
      { type: "inside", xAxisIndex: [0], start: 0, end: 100 },
      {
        type: "slider",
        xAxisIndex: [0],
        start: 0,
        end: 100,
        bottom: 5,
        height: 14,
        show: true,
        handleSize: 8,
        handleStyle: { color: '#5470c6' },
        backgroundColor: 'rgba(240, 242, 245, 0.8)',
        borderColor: '#dcdfe6',
        fillerColor: 'rgba(84, 112, 198, 0.3)'
      },
      ...yAxisDataZoom,
      ...yAxisSliderDataZoom
    ];

    // tooltip
    const tooltipOption = {
      trigger: "axis",
      axisPointer: {
        type: "cross",
        label: {
          backgroundColor: "#6a7985",
          formatter: (params) => {
            if (params.axisDimension === 'x') {
              const xVal = params.value;
              if (needCompactMultiSection) {
                for (const [sectionId, sectionInfo] of dataBySection) {
                  console.warn('sectionId',sectionId);
                  const startIdx = sectionStartIndex.get(sectionInfo.sectionIndex) || 0;
                  const aggregatedData = sectionAggregatedData.get(sectionInfo.sectionIndex) || [];
                  const dataIndex = Math.round(xVal - startIdx);
                  if (dataIndex >= 0 && dataIndex < aggregatedData.length) {
                    return aggregatedData[dataIndex]?.timestamp || '';
                  }
                }
                return '';
              } else {
                return formatValueToDateTime(xVal);
              }
            }
            if (params.axisDimension === 'y') {
              return typeof params.value === 'number' ? parseFloat(params.value.toFixed(2)).toString() : params.value;
            }
            return params.value;
          }
        },
      },
      confine: true,
      extraCssText: 'max-width: 350px; word-break: break-all;',
      textStyle: { fontSize: 13 },
      formatter: (params) => {
        let timeStr = '';
        if (params && params.length > 0) {
          if (needCompactMultiSection) {
            const xVal = params[0].axisValue;
            for (const [sectionId, sectionInfo] of dataBySection) {
              console.warn('sectionId',sectionId);
              const startIdx = sectionStartIndex.get(sectionInfo.sectionIndex) || 0;
              const aggregatedData = sectionAggregatedData.get(sectionInfo.sectionIndex) || [];
              const dataIndex = Math.round(xVal - startIdx);
              if (dataIndex >= 0 && dataIndex < aggregatedData.length) {
                timeStr = aggregatedData[dataIndex]?.timestamp || '';
                break;
              }
            }
          } else {
            const date = new Date(params[0].axisValue);
            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const d = String(date.getDate()).padStart(2, '0');
            const h = String(date.getHours()).padStart(2, '0');
            const mi = String(date.getMinutes()).padStart(2, '0');
            const s = String(date.getSeconds()).padStart(2, '0');
            timeStr = `${y}-${m}-${d} ${h}:${mi}:${s}`;
          }
        }
        let html = `<div style="margin-bottom:8px;font-weight:bold;border-bottom:1px solid #ddd;padding-bottom:5px;">${timeStr || ''}</div>`;
        params.forEach((item) => {
          const val = item.value !== null && item.value !== undefined
            ? typeof item.value[1] === 'number'
              ? formatValueRemove0(item.value[1])
              : formatValueRemove2(item.value[1])
            : '--';
          html += `
            <div style="display:flex;align-items:center;margin:6px 0;">
              <span style="display:inline-block;width:12px;height:12px;border-radius:50%;background:${item.color};margin-right:10px;"></span>
              <span style="flex:1;font-size:13px;">${item.seriesName}:</span>
              <span style="font-weight:bold;margin-left:12px;color:#2196f3;">${val}</span>
            </div>
          `;
        });
        return html;
      }
    };

    const option = {
      title: { text: t("message.reaTimePlot.deviceVariableMonitor") },
      legend: {
        data: legendData,
        selected: initialLegendSelected,
        top: legendTop,
        type: 'scroll',
        pageButtonItemGap: 3,
        pageButtonPosition: 'end',
        pageFormatter: '{current}/{total}',
        pageTextStyle: { fontSize: 12 },
        textStyle: { fontSize: 12 },
        padding: [0, 20, 0, 20],
        itemHeight: 10,
        itemWidth: 20,
        itemGap: 3,
        selector: false,
        inactiveColor: '#ccc'
      },
      xAxis: xAxisConfig,
      yAxis: yAxisConfig,
      series: allSeriesData,
      tooltip: tooltipOption,
      grid: {
        left: gridLeft,
        right: `${gridRight}px`,
        bottom: "3%",
        top: gridTop,
        containLabel: true,
      },
      dataZoom: dataZoomConfig,
      animation: false,
      animationDuration: 0
    };

    chartInstance.off('legendselectchanged');
    chartInstance.setOption(option, { notMerge: true });

    // 图例显隐
    chartInstance.on('legendselectchanged', function (params) {
      const selected = params.selected;
      const changedSeries = params.name;
      if (selected[changedSeries] !== undefined) {
        legendSelectedState.value[changedSeries] = selected[changedSeries];
      }
      const currentOption = chartInstance.getOption();
      const series = currentOption.series;
      const seriesIndex = series.findIndex(s => s.name === changedSeries);
      if (seriesIndex === -1) return;
      const isSelected = selected[changedSeries];
      const originalSeries = allSeriesData.find(s => s.name === changedSeries);
      const symbolType = originalSeries ? originalSeries.symbol : 'circle';
      const updatedSeries = series.map((seriesItem, index) => {
        if (index === seriesIndex) {
          return {
            ...seriesItem,
            lineStyle: { ...seriesItem.lineStyle, opacity: isSelected ? 1 : 0.2 },
            itemStyle: { ...seriesItem.itemStyle, opacity: isSelected ? 1 : 0.2 },
            symbol: isSelected ? symbolType : 'none',
            symbolSize: isSelected ? 6 : 0
          };
        }
        return seriesItem;
      });
      chartInstance.setOption({ series: updatedSeries }, { notMerge: false, replaceMerge: 'series', lazyUpdate: false });
    });

    setTimeout(() => {
      if (!needCompactMultiSection) {
        applyXAxisRangeToChart();
      }
      chartInstance.resize({ duration: 0 });
    }, 100);

  } catch (error) {
    console.error('Chart update failed:', error);
  }
};


const applyXAxisRangeToChart = () => {
  const validTimeRanges = getAllTimeRangesForQuery();
  if (validTimeRanges.length >= 2) return;
  if (!chartInstance) return;
  if (xAxisManualRange.value.enabled && xAxisManualRange.value.min && xAxisManualRange.value.max) {
    const minTimestamp = new Date(xAxisManualRange.value.min).getTime();
    const maxTimestamp = new Date(xAxisManualRange.value.max).getTime();
    chartInstance.setOption({
      xAxis: {
        min: minTimestamp,
        max: maxTimestamp
      }
    });
  } else {
    chartInstance.setOption({
      xAxis: {
        min: null,
        max: null
      }
    });
  }
};


 
// ===== X轴范围控制方法 =====
const openXAxisRangeSetting = () => {
  const validTimeRanges = getAllTimeRangesForQuery();
  
  // 如果是多时段，打开留白设置对话框
  if (validTimeRanges.length >= 2) {
    tempXAxisPadding.value = {
      left: xAxisPadding.value.left || 0,
      right: xAxisPadding.value.right || 0,
      sectionGap: xAxisPadding.value.sectionGap !== undefined ? xAxisPadding.value.sectionGap : 300
    };
    showXAxisPaddingDialog.value = true;
    return;
  }
  
  // 单时段：打开原有的时间范围设置
  if (xAxisManualRange.value.enabled) {
    tempXAxisRange.value = {
      min: xAxisManualRange.value.min,
      max: xAxisManualRange.value.max
    };
  } else {
    const currentData = getAllCachedData();
    if (currentData.length > 0) {
      tempXAxisRange.value = {
        min: currentData[0]?.timestamp || '',
        max: currentData[currentData.length - 1]?.timestamp || ''
      };
    } else {
      tempXAxisRange.value = { min: '', max: '' };
    }
  }
  showXAxisRangeDialog.value = true;
};
// ===== 保存X轴留白设置 =====

// ===== 在 script setup 顶部添加 =====
// 从 sessionStorage 加载 X轴留白设置
const loadXAxisPadding = () => {
  try {
    const saved = sessionStorage.getItem('history_xAxisPadding');
    if (saved) {
      const parsed = JSON.parse(saved);
      xAxisPadding.value = {
        left: parsed.left || 0,
        right: parsed.right || 0,
        sectionGap: parsed.sectionGap || 300
      };
    }
  } catch (e) {
    console.warn('加载X轴留白设置失败:', e);
  }
};

// 保存 X轴留白设置到 sessionStorage
const saveXAxisPaddingToStorage = () => {
  try {
    sessionStorage.setItem('history_xAxisPadding', JSON.stringify(xAxisPadding.value));
  } catch (e) {
    console.warn('保存X轴留白设置失败:', e);
  }
};

// ===== 修改 saveXAxisPadding 方法 =====
const saveXAxisPadding = () => {
  xAxisPadding.value = {
    left: tempXAxisPadding.value.left || 0,
    right: tempXAxisPadding.value.right || 0,
    sectionGap: tempXAxisPadding.value.sectionGap !== undefined ? tempXAxisPadding.value.sectionGap : 300
  };
  // 保存到 sessionStorage
  saveXAxisPaddingToStorage();
  showXAxisPaddingDialog.value = false;
  // 重新刷新图表
  updateChart(getAllCachedData());
  ElMessage.success(t('message.historyRecord.paddingSetSuccess'));
};


// ===== 修改 resetXAxisPadding 方法 =====
const resetXAxisPadding = () => {
  tempXAxisPadding.value = { left: 0, right: 0, sectionGap: 300 };
  xAxisPadding.value = { left: 0, right: 0, sectionGap: 300 };
  // 保存到 sessionStorage
  saveXAxisPaddingToStorage();
  showXAxisPaddingDialog.value = false;
  updateChart(getAllCachedData());
  ElMessage.success(t('message.historyRecord.paddingResetSuccess'));
};

// 保存X轴范围设置（单时段）
const saveXAxisRange = () => {
  const min = tempXAxisRange.value.min;
  const max = tempXAxisRange.value.max;
  
  if (!min || !max) {    
    ElMessage.success(t('message.historyRecord.selectValidTimeRange'));
    return;
  }
  
  const minTime = new Date(min).getTime();
  const maxTime = new Date(max).getTime();
  
  if (minTime >= maxTime) {
    ElMessage.success(t('message.historyRecord.startTimeLessThanEndTime'));    
    return;
  }
  
  xAxisManualRange.value = {
    min: min,
    max: max,
    enabled: true
  };
  
  showXAxisRangeDialog.value = false;
  
  // 应用X轴范围到图表
  applyXAxisRangeToChart();
  ElMessage.success(t('message.historyRecord.xAxisRangeSetSuccess', { min: min, max: max }));
};

// 重置X轴范围
const resetXAxisRange = () => {
  xAxisManualRange.value = { min: null, max: null, enabled: false };
  showXAxisRangeDialog.value = false;  
  applyXAxisRangeToChart(); 
  ElMessage.success(t('message.historyRecord.rangeXResetSuccess'));
};

// 清除X轴范围
const clearXAxisRange = () => {
  xAxisManualRange.value = { min: null, max: null, enabled: false };
  tempXAxisRange.value = { min: '', max: '' };

  tempXAxisPadding.value = { left: 0, right: 0,sectionGap:300 };
  xAxisPadding.value = { left: 0, right: 0,sectionGap:300 };
  
  const validTimeRanges = getAllTimeRangesForQuery();
  if (validTimeRanges.length >= 2) 
  {
    updateChart(getAllCachedData());
  }

  if (showXAxisRangeDialog.value) {
    showXAxisRangeDialog.value = false;
  }
  applyXAxisRangeToChart();
  ElMessage.success(t('message.historyRecord.XRangesCleared'));
};

// Y轴范围控制方法
const openYAxisRangeSetting = (variable) => {
  currentYAxisVariable.value = variable;
  const currentRange = yAxisManualRange.value[variable.name];
  if (currentRange && currentRange.enabled) {
    tempYAxisRange.value = {
      min: currentRange.min.toFixed(4),
      max: currentRange.max.toFixed(4)
    };
  } else {
    const currentData = getAllCachedData();
    const range = calculateYAxisRange(currentData, variable.name);
    tempYAxisRange.value = {
      min: range.min.toFixed(4),
      max: range.max.toFixed(4)
    };
  }
  showYAxisRangeDialog.value = true;
};

const saveYAxisRange = () => {
  if (!currentYAxisVariable.value) return;
  
  const min = parseFloat(tempYAxisRange.value.min);
  const max = parseFloat(tempYAxisRange.value.max);
  
  if (isNaN(min) || isNaN(max)) {
    ElMessage.warning(t('message.historyRecord.invalidRange'));
    return;
  }
  
  if (min >= max) {
    ElMessage.warning(t('message.historyRecord.minGreaterThanMax'));
    return;
  }
  
  yAxisManualRange.value[currentYAxisVariable.value.name] = {
    min: min,
    max: max,
    enabled: true
  };
  
  showYAxisRangeDialog.value = false;
  updateChart(getAllCachedData());
  ElMessage.success(t('message.historyRecord.rangeSetSuccess'));
};

const resetYAxisRange = (variableName) => {
  if (variableName) {
    delete yAxisManualRange.value[variableName];
  } else {
    yAxisManualRange.value = {};
  }
  updateChart(getAllCachedData());
  ElMessage.success(t('message.historyRecord.rangeResetSuccess'));
};

const clearAllYAxisRanges = () => {
  yAxisManualRange.value = {};
  updateChart(getAllCachedData());
  ElMessage.success(t('message.historyRecord.allRangesCleared'));
};

// 轴命令处理函数
const handleAxisCommand = (command) => {
  if (command === 'showYAxisSettings') {
    showYAxisRangePanel.value = true;
  } else if (command === 'showXAxisControl') {
    openXAxisRangeSetting();
  } else if (command === 'clearXAxisRange') {
    clearXAxisRange();
  } else if (command === 'clearAllYAxis') {
    clearAllYAxisRanges();
  }
};

const formatDeviceTree = (treeData) => {
  return treeData.map((item) => {
    const node = {
      id: item.id,
      name: item.name,
      type: item.type,
      key: item.key,
      deviceCode: item.data?.device_code || "",
      parentId: item.parentId,
      parentKey: item.parentKey,
    };

    if (item.children && item.children.length > 0) {
      node.children = formatDeviceTree(item.children);
    }

    return node;
  });
};

const loadDeviceTree = async () => {
  try {
    const params = { companyId: null };
    const res = await getCompanyDeviceTree(params);

    if (res.success) {
      deviceList.value = res.data.list;
      deviceTreeOptions.value = res.data.list?.length
        ? formatDeviceTree(res.data.list)
        : [];
    } else {
      ElMessage.error(res.msg || t("message.reaTimePlot.loadDeviceTreeFailed"));
    }
  } catch (error) {
    ElMessage.error(t("message.reaTimePlot.loadDeviceTreeFailed"));
  }
};

function fileTimeToFormat(fileTime) {
  const FILE_TIME_EPOCH_DIFF = 11644473600000;
  const jsTimestamp = fileTime / 10000 - FILE_TIME_EPOCH_DIFF;
  const date = new Date(jsTimestamp);
  if (isNaN(date.getTime())) {
    return "Invalid time";
  }
  
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');
  
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}
 
const processHistoryData = (apiData, varParm, sectionIndex, sectionId) => {
  try {
    if (!apiData?.list) return [];
    const processedData = [];
    
    const varParmMap = new Map();
    varParm.forEach(item => {
      varParmMap.set(item.var_name, {
        mult: 1,
        var_decimal: item.var_decimal || 0,
        minimum: item.minimum,
        maximum: item.maximum
      });
    });
    
    apiData.list.forEach(device => {
      if (!device.list?.length) return;
      
      device.list.forEach(item => {
        const varName = device.var_name;
        let varValue = parseFloat(item.var_value);
        const varConfig = varParmMap.get(varName);
        
        if (!varConfig) return;
        
        if (varConfig.mult !== 1 && !isNaN(varConfig.mult)) {
          varValue = varValue * varConfig.mult;
        }
        
        if (varConfig.var_decimal !== undefined && !isNaN(varConfig.var_decimal)) {
          varValue = varValue.toFixed(varConfig.var_decimal);
          varValue = parseFloat(varValue);
        }
        
        let isValid = true;
        const minValue = varConfig.minimum;
        const maxValue = varConfig.maximum;
        
        if (varValue === null || varValue === undefined || isNaN(varValue)) {
          isValid = false;
        }
        
        if (minValue !== null && minValue !== undefined && !isNaN(minValue)) {
          if (varValue < minValue) {
            isValid = false;
          }
        }
        
        if (maxValue !== null && maxValue !== undefined && !isNaN(maxValue)) {
          if (varValue > maxValue) {
            isValid = false;
          }
        }
        
        if (isValid) {
          const dataItem = {
            timestamp: fileTimeToFormat(item.timestamp),
            rawTime: new Date(item.timestamp),
            device_code: device.device_code,
            var_name: varName,
            [varName]: varValue,
            sectionIndex: sectionIndex,
            sectionId: sectionId
          };
          processedData.push(dataItem);
        }
      });
    });
    
    return processedData;
  } catch (error) {
    console.error("Data processing error:", error);
    ElMessage.error(t("message.reaTimePlot.dataProcessError"));
    return [];
  }
};

const updateCachedData = (newData) => {
  if (!newData || newData.length === 0) return;

  newData.forEach(item => {
    const key = `${item.timestamp}_${item.sectionId}`;
    if (cachedHistoryData.value.has(key)) {
      const existingItem = cachedHistoryData.value.get(key);
      Object.keys(item).forEach(key => {
        if (key !== 'timestamp' && key !== 'rawTime' && key !== 'sectionId' && key !== 'sectionIndex') {
          existingItem[key] = item[key];
        }
      });
    } else {
      cachedHistoryData.value.set(key, { ...item });
    }
  });
};

const getAllCachedData = () => {
  return Array.from(cachedHistoryData.value.values());
};

const formatTimeSend = (dateString) => {
  if (!dateString) return '';
  return dateString.replace(' ', 'T');
};

const decompressData = (compressedBase64) => {
  try {
    const compressedData = Uint8Array.from(atob(compressedBase64), c => c.charCodeAt(0));
    const decompressedData = pako.inflate(compressedData, { to: 'string' });
    return JSON.parse(decompressedData);
  } catch (error) {
    console.error('数据解压失败:', error);
    throw error;
  }
};

const handleApiResponse = async (response) => {
  if (response.data && response.success !== undefined) {
    const result = response;    
    if (result.success && result.data.compressed) {
      try {
        const decompressedList = decompressData(result.data.compressedData);
        return {
          ...result,
          data: {
            ...result.data,
            list: decompressedList,
            compressed: false
          }
        };
      } catch (error) {
        console.error('数据解压失败:', error);
        throw new Error('Data extraction failed');
      }
    }
    return result;
  }
  
  const result = await response.json();
  
  if (result.success && result.data.compressed) {
    try {
      const decompressedList = decompressData(result.data.compressedData);
      return {
        ...result,
        data: {
          ...result.data,
          list: decompressedList,
          compressed: false
        }
      };
    } catch (error) {
      console.error('数据解压失败:', error);
      throw new Error('Data extraction failed');
    }
  }
  return result;
};

const calculateMaxPointsPerDay = (totalDays) => {
  if (totalDays <= 1) return 2000;
  if (totalDays <= 3) return 1500;
  if (totalDays <= 8) return 1000;
  if (totalDays <= 15) return 500;
  if (totalDays <= 30) return 300;
  return 300;
};

const fetchHistoryData = async () => {
  loading.value = true;
  
  try {
    clearChart();
    const queryPromises = [];
    const deviceVariableMap = getDeviceVariableMap();
    const timeRanges = getAllTimeRangesForQuery();
    
    if (timeRanges.length === 0) {
      ElMessage.warning(t('message.historyRecord.selectTimeRange'));
      loading.value = false;
      return;
    }
    
    for (const timeRange of timeRanges) {
      const dailyTimeRanges = splitTimeRangeToDays(timeRange.start, timeRange.end);
      const maxPointsPerDay = calculateMaxPointsPerDay(dailyTimeRanges.length);
      const sectionIndex = timeRange.index;
      const sectionId = timeRange.id;
      
      

      for (const [deviceCode, deviceData] of deviceVariableMap) {
        const { var_names, companyId } = deviceData;      
        if (!var_names || var_names.length === 0) continue;
        
        for (const dailyRange of dailyTimeRanges) {
          const params = {
            lang: localStorage.getItem('lang') || 'zh-CN',
            company_id: companyId,
            device_code: deviceCode,
            slave_id: undefined,
            maxPoints: maxPointsPerDay,
            var_names: var_names,          
            ben_time: formatTimeSend(dailyRange.start),
            end_time: formatTimeSend(dailyRange.end),
            pageNum: pagination.currentPage,        
            pageSize: pagination.pageSize,
          };
          
          queryPromises.push(
            queryHistoryWhenAllData(params).then(async (res) => {
              if (res.success) {
                const resConvert = await handleApiResponse(res);
                return processHistoryData(resConvert.data, var_names, sectionIndex, sectionId);
              } else {
                console.error(`设备 ${deviceCode} ${dailyRange.start} 查询失败:`, res.msg);
               // return [];
              }
            }).catch(error => {
              console.error(`设备 ${deviceCode} ${dailyRange.start} 查询异常:`, error);
              return [];
            })
          );
        }
      }
    }
    
    const results = await Promise.all(queryPromises);
    const allData = results.flat();
    
    updateCachedData(allData);
    
    if (pagination.currentPage <= 1) {
      pagination.total = allData.length;
    }
    
    updateDisplayVariables();
    updateChart(getAllCachedData());
    
    if (allData.length === 0 && pagination.currentPage === 1) {
      ElMessage.info(t("message.historyRecord.noData"));
    } else {      
      const totalHours = getTotalHours();
      const sectionCount = timeRanges.length;
      ElMessage.success(t('message.historyRecord.queryDataSuccessMulti', { 
        count: allData.length, 
        sections: sectionCount,
        hours: totalHours.toFixed(1)
      }));
    }
  } catch (error) {
    console.error("查询历史数据失败:", error);
    ElMessage.error(t("message.historyRecord.queryError"));
  } finally {
    loading.value = false;
  }
};

const updateDisplayVariables = () => {
  if (selectedVarNames.value.length === 0) {
    currentDisplayVariables.value = allVariables.value;
  } else {
    currentDisplayVariables.value = allVariables.value.filter(variable => 
      selectedVarNames.value.includes(variable.name)
    );
  }
};
 
const formatLocalDateTime = (date) => {
  if (typeof date === 'string') {
    date = new Date(date);
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const formatDateString = (date) => {
  if (typeof date === 'string') {
    date = new Date(date);
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
};

const splitTimeRangeToDays = (startTime, endTime) => {
  const ranges = [];
  const start = new Date(startTime);
  const end = new Date(endTime);
  
  if (end - start <= 24 * 60 * 60 * 1000) {
    ranges.push({
      start: formatLocalDateTime(start),
      end: formatLocalDateTime(end),
      date: formatDateString(start)
    });
    return ranges;
  }
  
  let currentDay = new Date(start);
  currentDay.setHours(0, 0, 0, 0);
  
  while (currentDay <= end) {
    const dayStart = new Date(currentDay);
    const dayEnd = new Date(currentDay);
    dayEnd.setHours(23, 59, 59, 999);
    
    const actualStart = dayStart < start ? start : dayStart;
    const actualEnd = dayEnd > end ? end : dayEnd;
    
    if (actualStart <= actualEnd) {
      ranges.push({
        start: formatLocalDateTime(actualStart),
        end: formatLocalDateTime(actualEnd),
        date: formatDateString(actualStart)
      });
    }
    
    currentDay.setDate(currentDay.getDate() + 1);
  }
  
  return ranges;
};

const handleExport = async () => {
  if (selectedVarNames.value.length === 0) {
    ElMessage.error(t("message.varableSelectorControl.selectVariableFirst"));
    return;
  }

  if (Object.keys(filteredVarDevices.value).length === 0) {
    ElMessage.error(t("message.varableSelectorControl.selectVariableFirst"));
    return;
  }

  if (!hasValidTimeRanges()) {
    ElMessage.error(t("message.historyRecord.selectTimeRange"));
    return;
  }
  
  cachedHistoryData.value.clear();
  const totalHours = getTotalHours();
  if (totalHours > 17520) {
    ElMessage.error(t("message.reaTimePlot.exceedDay", {val: 730}));
    return;
  }

  pagination.currentPage = 1;
  exportLoading.value = true;
  await fetchHistoryDataExport();
  exportLoading.value = false;
};

const fetchHistoryDataExport = async () => {
  try {
    const exportPromises = [];
    const deviceVariableMap = getDeviceVariableMap();
    const timeRanges = getAllTimeRangesForQuery();

    for (const timeRange of timeRanges) {
      const dailyTimeRanges = splitTimeRangeToDays(timeRange.start, timeRange.end);
      
      for (const [deviceCode, deviceData] of deviceVariableMap) {
        const { var_names, companyId } = deviceData;
        if (!var_names || var_names.length === 0) continue;

        for (const dailyRange of dailyTimeRanges) {
          const params = {
            lang: localStorage.getItem('lang') || 'zh-CN',
            company_id: companyId,
            device_code: deviceCode,
            slave_id: undefined,
            var_names: var_names,
            ben_time: formatTimeSend(dailyRange.start),
            end_time: formatTimeSend(dailyRange.end),
            pageNum: 1,        
            pageSize: 10000,
          };
              
          exportPromises.push(
            exportHisVariablesByFile(params).then(response => {
              return { 
                deviceCode, 
                response, 
                date: dailyRange.date
              };
            }).catch(error => {
              console.error(`设备 ${deviceCode} ${dailyRange.date} 导出异常:`, error);
              return { 
                deviceCode, 
                error: true, 
                date: dailyRange.date,
                message: error.message 
              };
            })
          );
        }
      }
    }
    
    const results = await Promise.all(exportPromises);
    const exportResults = new Map();
    let successCount = 0;
    let failCount = 0;
    
    for (const result of results) {
      if (result.error) {
        failCount++;
        continue;
      }
      
      const { deviceCode, response, date } = result;
      
      if (!(response.data instanceof Blob)) {
        failCount++;
        continue;
      }
      
      if (!exportResults.has(deviceCode)) {
        exportResults.set(deviceCode, []);
      }
      
      exportResults.get(deviceCode).push({
        blob: response.data,
        date: date
      });
      
      successCount++;
    }
    
    for (const [deviceCode, dailyExports] of exportResults) {
      for (const exportData of dailyExports) {
        downloadExportFile(exportData.blob, deviceCode, exportData.date);
      }
    }
    
    if (successCount > 0) {
      ElMessage.success(`Successfully exported ${successCount} data files, failed ${failCount}`);
    } else {
      ElMessage.error('Export failed. Please check your network connection or contact the administrator');
    }
  } catch (error) {
    console.error("导出历史数据失败:", error);
    ElMessage.error(t("message.historyRecord.exportError"));
  }
};

const downloadExportFile = (blobData, deviceCode, date) => {
  const blob = new Blob([blobData], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  
  const fileName = `HistoryData_${deviceCode}_${date}.xlsx`;  
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  
  setTimeout(() => {
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, 100);
};

const handleXAxisVariableChange = () => {
  // 切换X轴变量时重置手动范围（如果有需要）
};

const handleQuery = async () => {
  resetData();
  if (selectedVarNames.value.length === 0) {
    ElMessage.error(t("message.varableSelectorControl.selectVariableFirst"));
    return;
  }

  if (Object.keys(filteredVarDevices.value).length === 0) {
    ElMessage.error(t("message.varableSelectorControl.selectVariableFirst"));
    return;
  }

  if (!hasValidTimeRanges()) {
    ElMessage.error(t("message.historyRecord.selectTimeRange"));
    return;
  }

  cachedHistoryData.value.clear();
  const totalHours = getTotalHours();
  if (totalHours > 17520) {
    ElMessage.error(t("message.reaTimePlot.exceedDay", {val: 730}));
    return;
  }

  pagination.currentPage = 1;
  await fetchHistoryData();
  isInitialLoad.value = false;
};

// ===== 修改 resetData 方法，不重置 xAxisPadding =====
const resetData = () => {  
  cachedHistoryData.value.clear();
  pagination.total = 0;
  pagination.currentPage = 1;  
  clearChart();
  legendSelectedState.value = {};
  xAxisManualRange.value = { min: null, max: null, enabled: false };
  // ===== 不再重置 xAxisPadding，保持用户设置 =====
  // xAxisPadding.value = { left: 0, right: 0, sectionGap: xAxisPadding.value.sectionGap !== undefined ? xAxisPadding.value.sectionGap : 100 };
};


// 生命周期钩子
onMounted(async () => {

  // 加载保存的 X轴留白设置
  loadXAxisPadding();

  loadOptions();
  initChart();
  await loadDeviceTree();
  
  if (props.deviceId) {
    currentDeviceId.value = props.deviceId;
  }
  
  if (allVariables.value.length > 0 && !xAxisVariable.value) {
    xAxisVariable.value = allVariables.value[0].name;
  }
  
  await initializeTimeRanges();
  if (hasValidTimeRanges()) {
    await handleQuery();
  }
});

onBeforeUnmount(() => {
  if (chartInstance) {
    chartInstance.dispose();
  }
});
</script>

<style scoped>
.historyrecord-container {
  padding: 12px;
  background-color: #f5f7fa;
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 16px;
  box-sizing: border-box;
}

.chart-container {
  background: #fff;
  padding: 12px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  flex: 1;
  min-height: 0;
  position: relative;
  overflow: hidden;
  width: 100%;
}

.chart {
  width: 100% !important;
  height: 100% !important;
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
}

.control-panel {
  background: #fff;
  padding: 12px 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
  height: auto;
  min-height: fit-content;
}

.filter-section {
  flex-shrink: 0;
}

/* 默认时间区域样式 */
.time-range-default {
  width: 100%;
}

/* 多时段管理对话框样式 */
.multi-time-range-container {
  max-height: 500px;
  overflow-y: auto;
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e4e7ed;
}

.dialog-title {
  font-weight: 500;
  font-size: 14px;
  color: #303133;
}

.time-ranges-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
}

.time-range-section {
  background: #fafafa;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  padding: 10px 12px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.section-index {
  font-size: 13px;
  font-weight: 500;
  color: #606266;
}

.section-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.dialog-footer-tip {
  margin-top: 16px;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.yaxis-toolbar {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 100;
}

.yaxis-range-list {
  padding: 16px;
}

.yaxis-range-item {
  margin-bottom: 20px;
  padding: 12px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fafafa;
}

.range-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.variable-name {
  font-weight: bold;
  font-size: 14px;
  color: #303133;
}

.range-item-values {
  display: flex;
  gap: 16px;
  margin-bottom: 12px;
  font-size: 12px;
  color: #606266;
}

.range-item-actions {
  display: flex;
  gap: 8px;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .filter-row {
    flex-wrap: wrap;
  }
  
  .section-controls {
    flex-direction: column;
    align-items: stretch;
  }
  
  .section-controls .el-select,
  .section-controls .el-date-picker {
    width: 100% !important;
  }
}

@media (max-width: 768px) {
  .historyrecord-container {
    padding: 8px;
    gap: 12px;
  }
  
  .chart-container {
    padding: 8px;
  }
  
  .control-panel {
    padding: 10px;
  }
  
  .filter-row {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filter-row .el-select,
  .filter-row .el-date-picker,
  .filter-row .el-button {
    width: 100% !important;
  }
  
  .time-range-section {
    padding: 8px;
  }
}

@media (max-width: 480px) {
  .historyrecord-container {
    padding: 4px;
    gap: 8px;
  }
  
  .chart-container {
    padding: 4px;
  }
  
  .control-panel {
    padding: 8px;
  }
}

:deep(.el-table__body-wrapper)::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

:deep(.el-table__body-wrapper)::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

:deep(.el-table__body-wrapper)::-webkit-scrollbar-thumb {
  background: #c0c4cc;
  border-radius: 3px;
}

:deep(.el-table__body-wrapper)::-webkit-scrollbar-thumb:hover {
  background: #909399;
}

:deep(.el-table) {
  font-size: 14px;
}

:deep(.el-table .el-table__cell) {
  padding: 8px 0;
}

:deep(.el-table .cell) {
  padding: 0 12px;
  line-height: 1.4;
}

:deep(.el-form-item__label) {
  font-weight: 500;
}

:deep(.echarts-for-vue .echarts-yAxis) {
  transition: none !important;
}

:deep(.echarts-for-vue .echarts-yAxis-line) {
  transition: none !important;
}

:deep(.echarts-for-vue .echarts-yAxis-label) {
  transition: none !important;
}

:deep(.echarts-for-vue .echarts-legend-item) {
  transition: none !important;
}

:deep(.echarts-for-vue .echarts-dataZoom-slider) {
  z-index: 1000 !important;
}
</style>
 